import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreVertIcon, MotionPlayIcon, TabDuplicateIcon, ModeOffOnIcon, DeleteIcon, StacksOutlineIcon, GraphIcon } from '@/components/icons'
import { cn } from '@/lib/utils'
import { useRoles, useDefinitionCounts } from '@/hooks'
import { CATEGORY_DOT_COLORS, CATEGORY_LABELS } from '@/types'
import type { WorkflowDefinitionSummary } from '@/types'

interface WorkflowDefinitionsListTableProps {
  definitions: WorkflowDefinitionSummary[]
  canDuplicate: boolean
  canToggle: boolean
  canTrigger: boolean
  canDelete: boolean
  onNavigate: (def: WorkflowDefinitionSummary) => void
  onDuplicate: (def: WorkflowDefinitionSummary) => void
  onToggleActive: (def: WorkflowDefinitionSummary) => void
  onTrigger: (def: WorkflowDefinitionSummary) => void
  onDelete: (def: WorkflowDefinitionSummary) => void
}

function ListRow({
  definition,
  roles,
  canDuplicate,
  canToggle,
  canTrigger,
  canDelete,
  onNavigate,
  onDuplicate,
  onToggleActive,
  onTrigger,
  onDelete,
}: {
  definition: WorkflowDefinitionSummary
  roles: { id: string; name: string }[] | undefined
  canDuplicate: boolean
  canToggle: boolean
  canTrigger: boolean
  canDelete: boolean
  onNavigate: (def: WorkflowDefinitionSummary) => void
  onDuplicate: (def: WorkflowDefinitionSummary) => void
  onToggleActive: (def: WorkflowDefinitionSummary) => void
  onTrigger: (def: WorkflowDefinitionSummary) => void
  onDelete: (def: WorkflowDefinitionSummary) => void
}) {
  const counts = useDefinitionCounts(definition.yamlContent)

  const roleLabel = useMemo(() => {
    if (!roles || definition.permittedRoleIds.length === 0) return null
    const nameById = new Map(roles.map((r) => [r.id, r.name]))
    const names = definition.permittedRoleIds.map((id) => nameById.get(id)).filter((n): n is string => !!n)
    if (names.length === 0) return null
    if (names.length <= 2) return names.join(', ')
    return `${names[0]}, ${names[1]} +${names.length - 2}`
  }, [roles, definition.permittedRoleIds])

  return (
    <tr
      className="group border-b border-border hover:bg-muted cursor-pointer transition-colors has-[[data-state=open]]:bg-muted"
      data-testid={`workflow-definition-row-${definition.id}`}
      onClick={() => onNavigate(definition)}
    >
      <td className="px-4 py-3 break-words">
        <span className="text-sm font-bold leading-[1.6] tracking-[0.015em] text-card-foreground">
          {definition.displayName}
        </span>
        <Badge variant="outline" className="ml-2 text-xs" style={{ borderColor: 'rgba(118,124,150,1)' }}>
          V{definition.version}
        </Badge>
      </td>

      <td className="px-4 py-3">
        <Badge variant="outline" className="text-xs" style={{ borderColor: 'rgba(118,124,150,1)' }}>
          {definition.isSystem ? 'System' : 'Custom'}
        </Badge>
      </td>

      <td className="px-4 py-3">
        <span className="flex items-center gap-2 text-sm text-card-foreground break-words">
          <span
            className={cn(
              'h-2 w-2 rounded-full shrink-0',
              CATEGORY_DOT_COLORS[definition.category ?? 'Uncategorized'],
            )}
          />
          {CATEGORY_LABELS[definition.category ?? 'Uncategorized']}
        </span>
      </td>

      <td className="px-4 py-3">
        <Badge
          variant="outline"
          className={cn(
            'text-xs font-bold leading-[1.6] tracking-[0.015em] rounded-full px-2.5',
            definition.isActive
              ? 'h-6 bg-active-badge-bg text-active-badge-fg hover:bg-active-badge-bg'
              : 'h-6 bg-inactive-badge-bg text-inactive-badge-fg hover:bg-inactive-badge-bg',
          )}
          style={{ borderColor: definition.isActive ? 'rgba(38,132,255,1)' : 'rgba(128,133,155,1)' }}
        >
          {definition.isActive ? 'Active' : 'Inactive'}
        </Badge>
      </td>

      <td className="px-4 py-3 text-sm text-card-foreground">
        {counts ? (
          <span className="flex items-center gap-1.5">
            <StacksOutlineIcon className="h-4 w-4" />
            {counts.phases}
          </span>
        ) : '—'}
      </td>

      <td className="px-4 py-3 text-sm text-card-foreground">
        {counts ? (
          <span className="flex items-center gap-1.5">
            <GraphIcon className="h-4 w-4" />
            {counts.nodes}
          </span>
        ) : '—'}
      </td>

      <td className="px-4 py-3 text-sm text-card-foreground break-words">
        {roleLabel ?? '—'}
      </td>

      <td className="px-4 py-3 max-w-[240px]">
        {definition.description ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-sm text-card-foreground truncate">{definition.description}</p>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p>{definition.description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-sm text-card-foreground">—</span>
        )}
      </td>

      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 justify-end">
          {canTrigger && (
            <Button
              className="h-8 rounded-lg px-3 gap-1.5 text-xs"
              disabled={!definition.isActive}
              onClick={(e) => {
                e.stopPropagation()
                onTrigger(definition)
              }}
            >
              <MotionPlayIcon className="h-4 w-4" />
              Trigger
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="shrink-0 text-foreground focus:outline-none p-1.5 rounded-lg hover:bg-muted data-[state=open]:bg-muted"
                aria-label={`More options for ${definition.displayName}`}
                data-testid={`workflow-menu-trigger-${definition.id}`}
              >
                <MoreVertIcon className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="bottom"
              align="end"
              sideOffset={4}
              onClick={(e) => e.stopPropagation()}
              className="border-none shadow-elevated rounded-lg py-2 px-0 gap-0.5 min-w-0"
            >
              {canDuplicate && (
                <DropdownMenuItem
                  onClick={() => onDuplicate(definition)}
                  className="h-10 px-4 text-card-foreground focus:text-card-foreground cursor-pointer font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
                >
                  <TabDuplicateIcon className="h-5 w-5" />
                  Duplicate
                </DropdownMenuItem>
              )}
              {canDelete && !definition.isSystem && !definition.isActive && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete(definition)}
                    className="h-10 px-4 text-card-foreground focus:text-card-foreground cursor-pointer font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
                  >
                    <DeleteIcon className="h-5 w-5" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
              {canToggle && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onToggleActive(definition)}
                    className="h-10 px-4 text-card-foreground focus:text-card-foreground cursor-pointer font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
                  >
                    <ModeOffOnIcon className="h-5 w-5" />
                    {definition.isActive ? 'Deactivate' : 'Activate'}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  )
}

export function WorkflowDefinitionsListTable({
  definitions,
  canDuplicate,
  canToggle,
  canTrigger,
  canDelete,
  onNavigate,
  onDuplicate,
  onToggleActive,
  onTrigger,
  onDelete,
}: WorkflowDefinitionsListTableProps) {
  const { data: roles } = useRoles()

  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-muted" data-testid="workflow-definitions-list-table">
      <table className="w-full text-left" aria-label="Workflow definitions">
        <thead>
          <tr className="border-b border-border bg-table-header">
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Definition Name</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Type</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Category</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Status</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Phases</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Nodes</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Available To</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Description</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {definitions.map((definition) => (
            <ListRow
              key={definition.id}
              definition={definition}
              roles={roles}
              canDuplicate={canDuplicate}
              canToggle={canToggle}
              canTrigger={canTrigger}
              canDelete={canDelete}
              onNavigate={onNavigate}
              onDuplicate={onDuplicate}
              onToggleActive={onToggleActive}
              onTrigger={onTrigger}
              onDelete={onDelete}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}
